/**
 * EmailConnectionSettings Component
 * TASK-270: Email OAuth integration for search
 *
 * Add to Account Settings page to allow users to connect
 * their Gmail or Microsoft 365 email for search indexing.
 */

import { useState, useEffect, useCallback } from 'react';
import { Mail, Loader2, CheckCircle, XCircle, RefreshCw, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { emailConnectionService } from '../../services/emailConnectionService';
import type {
  EmailProvider,
  EmailConnectionStatus,
  EmailProviderStatus,
} from '../../types/emailConnection.types';

// Provider configuration
const PROVIDERS: {
  id: EmailProvider;
  name: string;
  icon: string;
  description: string;
  color: string;
}[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    icon: '📧',
    description: 'Connect your Gmail account',
    color: 'hover:border-red-300 hover:bg-red-50',
  },
  {
    id: 'msgraph',
    name: 'Microsoft 365',
    icon: '📬',
    description: 'Connect Outlook / Office 365',
    color: 'hover:border-blue-300 hover:bg-blue-50',
  },
];

interface EmailConnectionSettingsProps {
  className?: string;
}

export function EmailConnectionSettings({ className }: EmailConnectionSettingsProps) {
  const [status, setStatus] = useState<EmailConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState<EmailProvider | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch connection status
  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await emailConnectionService.getStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Handle connect
  const handleConnect = useCallback(
    async (provider: EmailProvider) => {
      try {
        setIsConnecting(provider);
        setError(null);
        await emailConnectionService.connect(provider);
        await fetchStatus();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Connection failed');
      } finally {
        setIsConnecting(null);
      }
    },
    [fetchStatus]
  );

  // Handle disconnect
  const handleDisconnect = useCallback(
    async (provider: EmailProvider) => {
      if (!confirm('Are you sure you want to disconnect this email account?')) {
        return;
      }

      try {
        setError(null);
        await emailConnectionService.disconnect(provider);
        await fetchStatus();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Disconnect failed');
      }
    },
    [fetchStatus]
  );

  // Handle sync
  const handleSync = useCallback(async () => {
    try {
      setIsSyncing(true);
      setError(null);
      await emailConnectionService.triggerSync();
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  }, [fetchStatus]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  /**
   * TASK-811: one line saying what actually happened, from the two fields that together mean
   * something. `lastSync` alone cannot say it — the backend stamps it when a sync starts and
   * when one fails, so a mailbox whose OAuth grant was revoked keeps reporting an attempt
   * minutes old. "Last synced: 10 minutes ago" over six months of total failure is the same
   * class of lie this ticket removed from the connection time.
   */
  const describeSync = (s: EmailProviderStatus) => {
    if (s.syncStatus === 'syncing') return 'Syncing now…';
    if (!s.lastSync) return `Not synced yet — connected on ${formatDate(s.connectedAt)}`;
    if (s.syncStatus === 'failed') return `Last sync failed: ${formatDate(s.lastSync)}`;
    return `Last synced: ${formatDate(s.lastSync)}`;
  };

  const hasAnyConnection = status?.gmail?.connected || status?.msgraph?.connected;

  return (
    <div className={cn('bg-white rounded-xl border shadow-sm', className)}>
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Mail className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Email Search Integration</h3>
            <p className="text-sm text-gray-500">
              Connect your email to enable natural language search across your inbox
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <XCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Provider Cards */}
            <div className="grid gap-4 sm:grid-cols-2">
              {PROVIDERS.map((provider) => {
                const providerStatus = status?.[provider.id];
                const isConnected = providerStatus?.connected;
                const isConnectingThis = isConnecting === provider.id;

                return (
                  <div
                    key={provider.id}
                    className={cn(
                      'border rounded-lg p-4 transition-all',
                      isConnected ? 'border-green-200 bg-green-50' : 'border-gray-200',
                      !isConnected && provider.color
                    )}
                  >
                    {/* Provider Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{provider.icon}</span>
                        <div>
                          <div className="font-medium text-gray-900">{provider.name}</div>
                          {isConnected ? (
                            <div className="flex items-center gap-1 text-sm text-green-600">
                              <CheckCircle className="h-3.5 w-3.5" />
                              Connected
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">{provider.description}</div>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      {isConnected ? (
                        <button
                          onClick={() => handleDisconnect(provider.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Disconnect"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnect(provider.id)}
                          disabled={isConnectingThis}
                          className={cn(
                            'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                            'bg-white border border-gray-300 text-gray-700',
                            'hover:bg-gray-50',
                            isConnectingThis && 'opacity-50 cursor-not-allowed'
                          )}
                        >
                          {isConnectingThis ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Connect'
                          )}
                        </button>
                      )}
                    </div>

                    {/* Connected Details */}
                    {isConnected && providerStatus && (
                      <div className="mt-3 pt-3 border-t border-green-200 text-sm text-gray-600">
                        {providerStatus.email && (
                          <div className="truncate">{providerStatus.email}</div>
                        )}
                        {/* TASK-811: "Last synced" used to render `connected_at`, so a mailbox
                            that had never synced showed the date it was connected. `lastSync`
                            is an ATTEMPT time, so it is read together with `syncStatus` — a
                            failing mailbox stamps an attempt every 30 minutes and would
                            otherwise report perfect health. */}
                        <div
                          className={cn(
                            'text-xs mt-1',
                            providerStatus.syncStatus === 'failed'
                              ? 'text-amber-600'
                              : 'text-gray-400'
                          )}
                        >
                          {describeSync(providerStatus)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Sync Button */}
            {hasAnyConnection && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-500">
                  Emails are synced automatically every 30 minutes
                </div>
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                    'bg-gray-100 text-gray-700 hover:bg-gray-200',
                    isSyncing && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
                  {isSyncing ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            )}

            {/* Privacy Note */}
            <div className="text-xs text-gray-400 pt-2">
              Your emails are indexed locally and never shared. Only you can search your own
              emails. You can disconnect at any time.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EmailConnectionSettings;
